import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PersonalInfoStep from './steps/PersonalInfoStep';
import BusinessSizeStep from './steps/BusinessSizeStep';
import BusinessTypeStep from './steps/BusinessTypeStep';
import AddressStep from './steps/AddressStep';
import ServicesStep from './steps/ServicesStep';
import BusinessHoursStep from './steps/BusinessHoursStep';
import ProfessionalsStep from './steps/ProfessionalsStep';
import SuccessStep from './steps/SuccessStep';
import useOnboardingFlow from './hooks/useOnboardingFlow';
import logoSvg from '../../assets/logo.svg';

/**
 * Fluxo de criação de conta e onboarding do Bookly
 * Design inspirado nas melhores práticas do Instagram e X
 */
const AccountCreationFlow = () => {
  console.log('[DEBUG-SIGNUP] Renderizando AccountCreationFlow');
  
  const navigate = useNavigate();
  const {
    currentStep,
    totalSteps,
    accountData,
    loadingSubmit,
    error,
    completedSalonId,
    verificationSent,
    isEmailVerified,
    goToNextStep,
    goToPreviousStep,
    clearSavedData,
    sendVerificationEmail,
    checkEmailVerification
  } = useOnboardingFlow(navigate);
  
  // Log específico para depurar problema de perda de progresso na recarga
  useEffect(() => {
    console.log('[DEBUG-RELOAD] AccountCreationFlow montado, verificando armazenamento:', {
      navegadorURL: window.location.href,
      passoAtual: currentStep,
      temLocalStorage: !!localStorage.getItem('bookly_setup_data'),
      temSessionStorage: !!sessionStorage.getItem('bookly_setup_password'),
      temSenhaNosCredenciais: sessionStorage.getItem('signup_credentials_backup') ? JSON.parse(sessionStorage.getItem('signup_credentials_backup')).password !== undefined : false,
      timestamp: new Date().toISOString()
    });

    // Verificar se estamos vindo de uma recarga ou navegação
    const isReload = performance.navigation ? performance.navigation.type === 1 : false;
    console.log('[DEBUG-RELOAD] Tipo de carregamento:', {
      éRecarga: isReload,
      referrer: document.referrer,
      historicoLength: window.history.length
    });

    // Mostrar como está o objeto accountData
    console.log('[DEBUG-RELOAD] Estado atual de accountData:', {
      senha: accountData.password ? (accountData.password === '********' ? 'MASCARADA' : 'PRESENTE') : 'AUSENTE',
      email: accountData.email || 'AUSENTE',
      sensiveisPreenchidos: !!(accountData.email && accountData.password && accountData.password !== '********'),
      passo: currentStep
    });

    // Se temos o backup no sessionStorage mas não em accountData, tentar recuperar
    const credentialsBackup = sessionStorage.getItem('signup_credentials_backup');
    if (credentialsBackup && (!accountData.password || accountData.password === '********')) {
      try {
        const credentials = JSON.parse(credentialsBackup);
        console.log('[DEBUG-RELOAD] Encontrado backup de credenciais que poderia ser usado:', {
          temEmail: !!credentials.email,
          temSenha: !!credentials.password
        });
      } catch (e) {
        console.error('[DEBUG-RELOAD] Erro ao analisar backup de credenciais:', e);
      }
    }
  }, []);
  
  // Log quando o componente é montado ou quando há mudanças importantes
  useEffect(() => {
    console.log('[DEBUG-SIGNUP] AccountCreationFlow montado ou atualizado. Passo atual:', currentStep);
    
    // Verificar se estamos voltando à página depois de um refresh
    const pageLoadCount = parseInt(sessionStorage.getItem('signup_page_loads') || '0', 10);
    sessionStorage.setItem('signup_page_loads', (pageLoadCount + 1).toString());
    
    console.log('[DEBUG-SIGNUP] Contagem de carregamentos da página:', pageLoadCount + 1);
    
    // Log de dados sensíveis - remover em produção
    const dadosSensiveisPresentes = {
      temEmail: !!accountData.email,
      temSenha: !!accountData.password,
      senhaMascarada: accountData.password === '********',
      temNome: !!accountData.name,
      temBusinessName: !!accountData.businessName,
    };
    
    console.log('[DEBUG-SIGNUP] Verificação de dados sensíveis:', dadosSensiveisPresentes);
    
    // Verificar se há potenciais problemas no accountData
    const problemasDetectados = [];
    
    if (accountData.password === '********') {
      problemasDetectados.push('Senha está mascarada e será perdida no cadastro');
    }
    
    if (problemasDetectados.length > 0) {
      console.warn('[DEBUG-SIGNUP] Problemas detectados nos dados:', problemasDetectados);
    }
    
    return () => {
      console.log('[DEBUG-SIGNUP] AccountCreationFlow desmontado');
    };
  }, [currentStep, accountData]);
  
  // Renderizar etapa atual
  const renderCurrentStep = () => {
    console.log(`[DEBUG-SIGNUP] Renderizando passo ${currentStep} de ${totalSteps}`, {
      ...Object.keys(accountData).reduce((acc, key) => {
        if (key === 'professionals') {
          acc[key] = Array.isArray(accountData[key]) 
            ? { count: accountData[key].length }
            : accountData[key];
        } else if (key === 'password') {
          acc[key] = accountData[key] ? (accountData[key] === '********' ? 'MASCARADA' : 'PRESENTE') : 'AUSENTE';
        } else {
          acc[key] = accountData[key];
        }
        return acc;
      }, {})
    });
    
    // Wrapper para os callbacks das etapas para adicionar logs
    const onNextWithLogging = (data) => {
      console.log(`[DEBUG-SIGNUP] Concluindo passo ${currentStep}, enviando dados para o próximo:`, {
        campos: Object.keys(data),
        temSenha: !!data.password,
        senhaMascarada: data.password === '********'
      });
      
      // Se está no primeiro passo e tem dados sensíveis, salvar em sessionStorage como backup
      if (currentStep === 1 && data.password && data.email) {
        console.log('[DEBUG-SIGNUP] Salvando backup de credenciais no sessionStorage');
        try {
          sessionStorage.setItem('signup_credentials_backup', JSON.stringify({
            email: data.email,
            password: data.password
          }));
        } catch (e) {
          console.error('[DEBUG-SIGNUP] Erro ao salvar backup:', e);
        }
      }
      
      goToNextStep(data);
    };
    
    const onBackWithLogging = () => {
      console.log(`[DEBUG-SIGNUP] Voltando do passo ${currentStep} para o passo anterior`);
      goToPreviousStep();
    };
    
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            initialData={accountData}
            onNext={onNextWithLogging}
          />
        );
      case 2:
        return (
          <BusinessSizeStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
          />
        );
      case 3:
        return (
          <BusinessTypeStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
          />
        );
      case 4:
        return (
          <AddressStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
          />
        );
      case 5:
        return (
          <ServicesStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
            businessType={accountData.businessType}
          />
        );
      case 6:
        return (
          <BusinessHoursStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
          />
        );
      case 7:
        return (
          <ProfessionalsStep
            initialData={accountData}
            onNext={onNextWithLogging}
            onBack={onBackWithLogging}
            categories={accountData.categories}
            isLoading={loadingSubmit}
          />
        );
      case 8:
        console.log('[DEBUG-SIGNUP] Renderizando tela de sucesso, processo concluído');
        return (
          <SuccessStep
            salonId={completedSalonId}
            businessName={accountData.businessName}
            verificationSent={verificationSent}
            isEmailVerified={isEmailVerified}
            sendVerificationEmail={sendVerificationEmail}
            checkEmailVerification={checkEmailVerification}
          />
        );
      default:
        console.error('[DEBUG-SIGNUP] Passo inválido:', currentStep);
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      {/* Header com logo */}
      <header className={`bg-white shadow-sm py-4 ${currentStep === totalSteps ? 'border-b border-gray-200' : ''}`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center">
            <img src={logoSvg} alt="Bookly" className="h-14" />
          </div>
        </div>
      </header>
      
      {/* Conteúdo principal */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className={`max-w-2xl mx-auto bg-white rounded-lg ${currentStep < totalSteps ? 'shadow-lg' : ''} overflow-hidden`}>
          {/* Card da etapa atual */}
          <div className="p-6 md:p-8">
            {renderCurrentStep()}
          </div>
        </div>
        
        {/* Mensagem de erro */}
        {error && (
          <div className="max-w-2xl mx-auto mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Erro! </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-white py-4 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Bookly · Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
};

export default AccountCreationFlow; 